import React from 'react';
import { UploadCloud, Trash2 } from 'lucide-react';
import Modal from '../../../../components/Modal';
import { Button } from '../../../../components';
import { useDutyRoster } from '../DutyRosterContext';
import { groupChangesByType } from '../changeMeta';

// Opened from the header "Publish" button. Shows a clear, grouped summary of
// every unpublished edit so the manager knows exactly what is about to be sent
// to the system, then confirms to sync. Mirrors the Modal / Tailwind styling of
// the export + draft-changes modals.
const PublishReviewModal = () => {
    const { publishModalOpen, setPublishModalOpen, changes, handlePublish, publishLoading, removeOneChange } = useDutyRoster();
    const groups = groupChangesByType(changes);

    return (
        <Modal
            isOpen={publishModalOpen}
            onClose={() => { if (!publishLoading) setPublishModalOpen(false); }}
            title="Review & publish changes"
            maxWidth="max-w-2xl"
            footer={(
                <div className="flex items-center justify-between gap-3">
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={() => setPublishModalOpen(false)}
                        disabled={publishLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        variant="primary"
                        onClick={handlePublish}
                        disabled={changes.length === 0 || publishLoading}
                        loading={publishLoading}
                        leftIcon={<UploadCloud className="w-4 h-4" />}
                    >
                        {publishLoading ? 'Publishing…' : `Publish ${changes.length} change${changes.length === 1 ? '' : 's'}`}
                    </Button>
                </div>
            )}
        >
            <div className="space-y-4 pt-1">
                <p className="text-[12px] text-[#64748B] dark:text-gray-400 leading-relaxed">
                    These edits are saved only in your browser so far. Review what's changing below, then
                    publish to sync them to the system. You can still cancel and keep editing.
                </p>

                {/* Per-type summary chips */}
                {changes.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                        {groups.map((g) => (
                            <span key={g.type} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-bold ${g.meta.cls}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${g.meta.dot}`} />
                                {g.meta.group} · {g.items.length}
                            </span>
                        ))}
                    </div>
                )}

                {/* Grouped detail */}
                {changes.length === 0 ? (
                    <div className="py-10 text-center text-[13px] text-[#94A3B8] dark:text-gray-500">No unpublished changes to publish.</div>
                ) : (
                    <div className="space-y-3">
                        {groups.map((g) => (
                            <div key={g.type} className="rounded-xl border border-[#E2E8F0] dark:border-gray-800 overflow-hidden">
                                <div className="flex items-center justify-between px-4 py-2.5 bg-[#F8FAFC] dark:bg-[#20242c] border-b border-[#EDF1F6] dark:border-gray-800">
                                    <span className="flex items-center gap-2 text-[12px] font-extrabold text-[#334155] dark:text-gray-200">
                                        <span className={`w-2 h-2 rounded-full ${g.meta.dot}`} />
                                        {g.meta.group}
                                    </span>
                                    <span className="text-[11px] font-bold text-[#94A3B8] dark:text-gray-500">{g.items.length}</span>
                                </div>
                                <ul className="divide-y divide-[#EDF1F6] dark:divide-gray-800">
                                    {g.items.map((c) => (
                                        <li key={c.id} className="flex items-center gap-3 px-4 py-2.5">
                                            <span className="flex-1 min-w-0 text-[12px] text-[#475569] dark:text-gray-400">{c.label}</span>
                                            <button
                                                type="button"
                                                onClick={() => removeOneChange(c.id)}
                                                disabled={publishLoading}
                                                title="Remove this change"
                                                className="shrink-0 w-7 h-7 grid place-items-center rounded-md border border-[#E2E8F0] dark:border-gray-700 text-[#94A3B8] dark:text-gray-500 hover:text-rose-600 hover:border-rose-200 dark:hover:border-red-900/50 hover:bg-rose-50 dark:hover:bg-red-900/10 transition disabled:opacity-40"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default PublishReviewModal;
